/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import Input from "../../../../../../library/input";
import { Title } from "../../../../../../library/title";
import { useTimelineContext } from "../../remotion-timeline/context/use-timeline-context";
import Caption from "./caption";
import { ContentWrapper, ListWrapper, SearchWrapper } from "./styled";
import { Virtuoso } from "react-virtuoso";
import { PlayerRef } from "@remotion/player";

interface TranscriptProps {
  playerRef: React.RefObject<PlayerRef>;
}

const Transcript = ({ playerRef }: TranscriptProps) => {
  const scrollingContainerRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { tracks } = useTimelineContext();
  const [containerHeight, setContainerHeight] = useState(0);
  const [currentFrame, setCurrentFrame] = useState(0);

  const [tick, setTick] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTick((t) => t + 1);
    }, 100);

    return () => clearInterval(intervalId);
  }, []);

  const allCaptions = useMemo(() => {
    const textItems = tracks.flatMap((track) =>
      track.items.filter((item) => item.type === "text")
    );
    return [...textItems].sort((a, b) => a.from - b.from);
  }, [tracks]);

  const captions = searchTerm
    ? allCaptions.filter((caption) =>
        caption.text?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allCaptions;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateContainerSize = () => {
    if (scrollingContainerRef.current) {
      setContainerHeight(scrollingContainerRef.current.offsetHeight);
    }
  };

  useEffect(() => {
    updateContainerSize();
    window.addEventListener("resize", updateContainerSize);
    return () => {
      window.removeEventListener("resize", updateContainerSize);
    };
  }, [updateContainerSize]);

  useEffect(() => {
    if (!playerRef.current) return;

    const handleFrameChange = () => {
      if (playerRef.current) {
        setCurrentFrame(playerRef.current.getCurrentFrame());
      }
    };

    const intervalId = setInterval(handleFrameChange, 100);

    playerRef.current.addEventListener("timeupdate", handleFrameChange);
    playerRef.current.addEventListener("seeked", handleFrameChange);

    return () => {
      clearInterval(intervalId);
      if (playerRef.current) {
        playerRef.current.removeEventListener("timeupdate", handleFrameChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        playerRef.current.removeEventListener("seeked", handleFrameChange);
      }
    };
  }, [playerRef]);

  const isCaptionActive = (caption: any) => {
    const actualCurrentFrame = playerRef.current
      ? playerRef.current.getCurrentFrame()
      : currentFrame;

    return (
      actualCurrentFrame >= caption.from &&
      actualCurrentFrame < caption.from + caption.durationInFrames
    );
  };

  useEffect(() => {
    if (!virtuosoRef.current) return;

    const activeIndex = captions.findIndex((caption) =>
      isCaptionActive(caption)
    );

    if (activeIndex !== -1) {
      setTimeout(() => {
        if (!virtuosoRef.current) return;
        virtuosoRef.current.scrollToIndex({
          index: activeIndex,
          align: "center",
          behavior: "smooth",
        });
      }, 100);
    }
  }, [currentFrame, captions, tick, isCaptionActive]);

  return (
    <ContentWrapper>
      <Title size="xs">Undertekster</Title>
      <SearchWrapper>
        <Input
          onChange={(e: any) => setSearchTerm(e.target.value)}
          placeholder="Søk etter tekst.."
          variant="search"
          value={searchTerm}
        />
      </SearchWrapper>
      <ListWrapper ref={scrollingContainerRef}>
        <Virtuoso
          ref={virtuosoRef}
          style={{ height: containerHeight }}
          totalCount={captions.length}
          itemContent={(index) => (
            <Caption
              data={captions[index]}
              key={captions[index].id}
              active={isCaptionActive(captions[index])}
              playerRef={playerRef}
            />
          )}
        />
      </ListWrapper>
    </ContentWrapper>
  );
};

export default Transcript;
